import { useState, useEffect, useCallback } from "react";

/**
 * Hook to handle PWA install prompt
 * Captures the beforeinstallprompt event and provides methods to trigger installation
 */
// Detect iOS device
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

export const usePWAInstallPrompt = () => {
  const [isInstallPromptSupported, setIsInstallPromptSupported] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  // Check if device is iOS
  useEffect(() => {
    setIsIOSDevice(isIOS());
  }, []);

  // Check if app is already installed
  useEffect(() => {
    const checkIfAppIsInstalled = () => {
      const isInstalled =
        window.matchMedia("(display-mode: standalone)").matches ||
        navigator.standalone;

      setIsAppInstalled(isInstalled);
    };

    checkIfAppIsInstalled();

    // Also listen for display mode changes
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const handleChange = (e) => setIsAppInstalled(e.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Listen for beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      // Prevent the mini-infobar from appearing on mobile
      event.preventDefault();
      // Store the event so it can be triggered later
      setDeferredPrompt(event);
      setIsInstallPromptSupported(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // For iOS devices, show install prompt even without beforeinstallprompt event
    if (isIOS() && !navigator.standalone) {
      setIsInstallPromptSupported(true);
    }

    // Listen for successful installation
    window.addEventListener("appinstalled", () => {
      setIsAppInstalled(true);
      setDeferredPrompt(null);
      setIsInstallPromptSupported(false);
    });

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  // Function to show the install prompt
  const showInstallPrompt = useCallback(() => {
    console.log("showInstallPrompt called", { deferredPrompt: !!deferredPrompt, isInstallPromptSupported });
    
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        deferredPrompt.userChoice.then((choiceResult) => {
          if (choiceResult.outcome === "accepted") {
            console.log("User accepted the install prompt");
          } else {
            console.log("User dismissed the install prompt");
          }
          setDeferredPrompt(null);
          setIsInstallPromptSupported(false);
        }).catch((error) => {
          console.error("Error in userChoice promise:", error);
          setDeferredPrompt(null);
          setIsInstallPromptSupported(false);
        });
      } catch (error) {
        console.error("Error calling deferredPrompt.prompt():", error);
        setDeferredPrompt(null);
        setIsInstallPromptSupported(false);
      }
    } else {
      console.warn("deferredPrompt is null - install prompt not available. This can happen if:");
      console.warn("1. The beforeinstallprompt event hasn't fired yet");
      console.warn("2. The app doesn't meet PWA installability criteria");
      console.warn("3. The app is already installed");
      console.warn("4. The browser doesn't support PWA installation");
    }
  }, [deferredPrompt, isInstallPromptSupported]);

  return { isInstallPromptSupported, showInstallPrompt, isAppInstalled, isIOSDevice };
};

export default usePWAInstallPrompt;
